import { Nullable, timeUtils } from '@sato/components';
import { endOfDay, isAfter, isWithinInterval } from 'date-fns';
import { sortBy } from 'lodash';

import { ApartmentStatus } from '@models/apartment.types';
import { SerializedPlainDate } from '@models/api/data.types';
import { Apartment, Article, MediaAsset } from '@models/satofi/apartment.types';
import { ApartmentProduct } from '@models/satofi/apartmentProduct.types';
import { RealEstate } from '@models/satofi/realEstate.types';
import { TranslatorFn } from '@stores/localization';

export const sortApartmentAssets = (assets: MediaAsset[] = []) => {
  const sortOrder: MediaAsset['type'][] = ['PRIMARY', 'DEFAULT', 'FLOORPLAN'];
  return assets.sort(
    (a, b) => sortOrder.indexOf(a.type) - sortOrder.indexOf(b.type)
  );
};

export const isImageMediaAsset = (
  asset: MediaAsset
): asset is ImageMediaAsset => asset.mimeType.includes('image');

export interface ImageMediaAsset extends MediaAsset {
  mimeType: 'image/${string}';
}

/**
 * Type guard for checking that the given campaign is currently active.
 * Prefer using server datetime for comparison.
 */
export const isCampaignActive = (
  campaign: Nullable<Article>,
  currentTime = new Date()
): campaign is Article => {
  if (!campaign?.validity) return false;

  const { start: campaignStart, end: campaignEnd } = campaign.validity;

  return isWithinInterval(currentTime, {
    start: new Date(campaignStart),
    end: endOfDay(new Date(campaignEnd)),
  });
};

/**
 * Filter active campaigns from the given list and sort their media by media
 * asset index. Useful for parsing campaign media to be included in apartment
 * media carousels
 */
export const getActiveCampaignMedia = (
  campaigns: Nullable<Article>[],
  currentDate = new Date()
) => {
  const activeCampaignMedia = [...(campaigns ?? [])]
    .filter((campaign) => isCampaignActive(campaign, currentDate))
    .flatMap(({ media }) => media ?? []);

  // sort campaign media after flattening all campaigns
  return sortBy(activeCampaignMedia, 'index');
};

export const getApartmentProductMedia = ({
  apartment,
  realEstate,
}: ApartmentProduct): MediaAsset[] | undefined => {
  const apartmentMedia = apartment.mediaAssets.filter(isImageMediaAsset);
  const realEstateMedia = realEstate.mediaAssets.filter(isImageMediaAsset);
  // use first campaign media asset as primary apartment media according to current agreed business logic
  const [firstCampaignImageAsset] = getActiveCampaignMedia(
    apartment.campaigns,
    new Date()
  ).filter(isImageMediaAsset);

  const assetsWithoutFloorPlan = apartmentMedia.filter(
    (asset) => asset.type !== 'FLOORPLAN'
  );
  const assetsWithFloorPlanOnly = apartmentMedia.filter(
    (asset) => asset.type === 'FLOORPLAN'
  );

  // prioritize apartment media but show apartment floor plan only
  // if there is no other media, even from the real-estate
  let sortedAssets = [
    ...sortApartmentAssets(assetsWithoutFloorPlan),
    ...realEstateMedia,
  ];

  if (firstCampaignImageAsset)
    sortedAssets = [firstCampaignImageAsset, ...sortedAssets];

  const media = [...sortedAssets, ...assetsWithFloorPlanOnly];

  if (!media.length) return undefined;

  // prioritize apartment floor plan for "apartment-type" variant and then other media
  return media;
};

export type ApartmentAvailability =
  | { status: 'reserved' }
  | { status: 'available' }
  | { status: 'availableSoon' }
  | { status: 'availableFrom'; availableFromDate: Date };

export const getApartmentAvailability = (
  status: ApartmentStatus,
  freeDate?: SerializedPlainDate | undefined | null,
  now: Date = new Date()
): ApartmentAvailability => {
  // Starting with v2.0.0-beta.1 date-fns doesn't accept strings as date arguments.
  if (
    (status === 'GOING_TO_BE_FREE' || status === 'FREE') &&
    freeDate &&
    isAfter(new Date(freeDate), now)
  ) {
    return { status: 'availableFrom', availableFromDate: new Date(freeDate) };
  } else if (status === 'GOING_TO_BE_FREE') {
    return { status: 'availableSoon' };
  } else if (status === 'FREE') {
    return { status: 'available' };
  }
  return { status: 'reserved' };
};

export const getApartmentPromotionText = (
  { campaigns }: Apartment,
  currentDate = new Date()
) => {
  const validCampaigns = campaigns?.filter((campaign) =>
    isCampaignActive(campaign, currentDate)
  );

  if (!validCampaigns || validCampaigns.length < 1) return undefined;

  return validCampaigns[0]?.items?.find(({ type }) => type === 'HEADER')?.text;
};

export const formatApartmentAvailabilityText = ({
  apartment: { state },
  translate,
}: {
  apartment: Pick<Apartment, 'state' | 'showTimes'>;
  translate: TranslatorFn;
}) => {
  const availability = getApartmentAvailability(state.status, state.available);

  if (availability.status === 'availableFrom') {
    return translate('apartmentAvailabilityStatus.availableFrom', {
      date: timeUtils.formatDate({
        date: availability.availableFromDate,
        dateFormat: timeUtils.DATE_FORMATS.DATE_SHORT,
      }),
    });
  }

  return translate(`apartmentAvailabilityStatus.${availability.status}`);
};

/** Keys for rendering apartment flags and concepts */
export const apartmentFlagKeys = [
  'flexHome',
  'renovated',
  'new',
  'ara',
  'sustainableChoice',
  'studio',
  'studentHome',
  'seniorApartment',
  'greenHomes',
] as const;

export type ApartmentFlagKey = (typeof apartmentFlagKeys)[number];

export const getApartmentFlagKeys = (
  {
    flags,
    renovation,
    flexHome,
  }: Pick<Apartment, 'flags' | 'renovation' | 'flexHome'>,
  realEstate?: Pick<RealEstate, 'flags' | 'concept'>
): ApartmentFlagKey[] => {
  // NOTE: the order matters
  const flagsMap: Record<ApartmentFlagKey, boolean> = {
    ara: flags.isAra,
    flexHome: !!flexHome?.isFlexHome,
    sustainableChoice: !!realEstate?.flags?.sustainableChoice,
    studio: realEstate?.concept === 'STUDIO_HOME',
    studentHome: !!flags.studentHome,
    seniorApartment: !!flags.seniorApartment,
    new: flags.isNew,
    renovated: !!renovation?.renovated,
    greenHomes: !!realEstate?.flags.greenHomes,
  };

  return Object.keys(flagsMap).filter((k): k is ApartmentFlagKey =>
    Boolean(flagsMap[k as ApartmentFlagKey])
  );
};
